import i18n from 'i18next'
import MULTIPLE_ATTENDEE_HIGHEST_ICON from '@public/assets/multiple_attendee_highest.svg'
import MULTIPLE_ATTENDEE_LOWER_ICON from '@public/assets/multiple_attendee_lower.svg'

/**
 * Extract the duplicate_entry validation from a time entry.
 * The backend currently nests it under metadata.validations; fall back to a
 * top-level validations field for forward compatibility.
 * Returns null when the entry has no multiple attendee meeting validation.
 */
export const getDuplicateEntryValidation = (item) => {
  const duplicateEntry =
    item?.metadata?.validations?.duplicate_entry ??
    item?.validations?.duplicate_entry
  if (!duplicateEntry || typeof duplicateEntry.is_highest_rate !== 'boolean') {
    return null
  }
  return duplicateEntry
}

/**
 * Returns the multiple attendee icon for the entry, or null when not applicable.
 * Blue icon when the current timekeeper has the highest billing rate,
 * red icon otherwise.
 */
export const getDuplicateEntryIcon = (item) => {
  const duplicateEntry = getDuplicateEntryValidation(item)
  if (!duplicateEntry) return null
  return duplicateEntry.is_highest_rate
    ? MULTIPLE_ATTENDEE_HIGHEST_ICON
    : MULTIPLE_ATTENDEE_LOWER_ICON
}

/**
 * Builds the tooltip text for the multiple attendee icon.
 */
export const getDuplicateEntryTooltip = (item) => {
  const duplicateEntry = getDuplicateEntryValidation(item)
  if (!duplicateEntry) return ''
  if (duplicateEntry.is_highest_rate) {
    return i18n.t('tooltip.multipleAttendeeHighest')
  }
  return i18n.t('tooltip.multipleAttendeeLower', {
    name: duplicateEntry.highest_rate_tkpr_name || '',
  })
}

// ── validate → save pass-through ────────────────────────────────────────────
// The KMK validate API reports which time entries belong to a multiple-attendee
// (duplicate) group via results[].findings[].duplicate_time_entry_ids. We cache
// the ids from the latest validate response and send them once, at the top level
// of the next create / patch / merge payload, then clear (consume-once).
let pendingDuplicateTimeEntryIds = []

/**
 * Flattens and de-duplicates duplicate_time_entry_ids from a validate response.
 */
export const extractDuplicateTimeEntryIds = (response) => {
  const results = Array.isArray(response?.results) ? response.results : []
  const ids = results.flatMap((result) => {
    const findings = Array.isArray(result?.findings) ? result.findings : []
    return findings.flatMap((finding) =>
      Array.isArray(finding?.duplicate_time_entry_ids)
        ? finding.duplicate_time_entry_ids
        : []
    )
  })
  return Array.from(
    new Set(ids.filter((id) => id !== null && id !== undefined))
  )
}

/**
 * Caches the duplicate ids from the latest validate response (overwrites).
 */
export const recordDuplicateTimeEntryIds = (response) => {
  pendingDuplicateTimeEntryIds = extractDuplicateTimeEntryIds(response)
}

/**
 * Returns { duplicate_time_entry_ids: [...] } for the pending ids and clears
 * them (consume-once). Returns {} when there is nothing pending.
 */
export const consumeDuplicateTimeEntryIdsPayload = () => {
  if (!pendingDuplicateTimeEntryIds.length) return {}
  const payload = { duplicate_time_entry_ids: pendingDuplicateTimeEntryIds }
  pendingDuplicateTimeEntryIds = []
  return payload
}
